// src/components/student/OnboardingModal.jsx
// Shown on the dashboard when a student has no board/grade yet.
// Captures board + standard so relevant materials can surface for them.
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { doc, getDocs, collection, updateDoc } from "firebase/firestore";
import { HiAcademicCap } from "react-icons/hi";
import { db } from "../firebase/firebase";

const BOARDS = [
  { id: "state", name: "State Board", subtitle: "Tamil Nadu State Board" },
  { id: "cbse", name: "CBSE", subtitle: "Central Board" },
  { id: "competitive", name: "Competitive", subtitle: "JEE · NEET · TNPSC" },
];

const sortByOrder = (arr) => [...arr].sort((a, b) => (a.order || 0) - (b.order || 0));

export default function OnboardingModal({ uid, onDone }) {
  const [board, setBoard] = useState("");
  const [grades, setGrades] = useState([]);
  const [grade, setGrade] = useState("");
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!board) { setGrades([]); setGrade(""); return; }
    setLoadingGrades(true);
    getDocs(collection(db, "curriculum", board, "grades"))
      .then((snap) => setGrades(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() })))))
      .catch(() => setGrades([]))
      .finally(() => setLoadingGrades(false));
    setGrade("");
  }, [board]);

  const handleSave = async () => {
    if (!board || !grade) return;
    setSaving(true);
    setError("");
    try {
      await updateDoc(doc(db, "students", uid), { board, grade });
      onDone();
    } catch (e) {
      console.error("Failed to save onboarding", e);
      setError("Couldn't save your details. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Brand ribbon — matches the auth split-screen gradient */}
        <div className="h-2 bg-gradient-to-r from-indigo-500 via-blue-500 to-sky-400" />
        <div className="p-6 sm:p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mb-3">
            <HiAcademicCap className="text-3xl text-indigo-600" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-800">Let's set up your learning</h2>
          <p className="text-sm text-gray-400 mt-1">Tell us what you're studying so we can show the right materials.</p>
        </div>

        {/* Board */}
        <label className="block text-xs font-semibold text-gray-500 mb-2">Your Board</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
          {BOARDS.map((b) => (
            <button
              key={b.id}
              onClick={() => setBoard(b.id)}
              className={`text-left border-2 rounded-xl p-3 transition-all ${
                board === b.id ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <p className="font-bold text-sm text-gray-800">{b.name}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{b.subtitle}</p>
            </button>
          ))}
        </div>

        {/* Grade */}
        <label className="block text-xs font-semibold text-gray-500 mb-2">Your Class / Standard</label>
        <select
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          disabled={!board || loadingGrades}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-50 mb-6"
        >
          <option value="">{loadingGrades ? "Loading…" : !board ? "Select a board first" : "Select your class"}</option>
          {grades.map((g) => (
            <option key={g.id} value={g.label}>{g.name}</option>
          ))}
        </select>

        {error && (
          <p className="text-xs text-red-500 mb-3 text-center">{error}</p>
        )}

        <button
          onClick={handleSave}
          disabled={!board || !grade || saving}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {saving && (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          {saving ? "Saving…" : "Start Learning"}
        </button>
        </div>
      </motion.div>
    </div>
  );
}
